import { useEffect, useRef } from "react";

/**
 * @typedef {Object} IndexedDBTamperDetectionOptions
 * @property {string} dbName - Name of the IndexedDB database.
 * @property {string} storeName - Name of the object store.
 * @property {string[]} keysToMonitor - List of keys to monitor for changes.
 * @property {number} [pollInterval=5000] - Polling interval in milliseconds.
 * @property {(key: string) => void} [onTamperDetected] - Optional callback when tampering is detected.
 */

/**
 * Custom hook to detect possible IndexedDB tampering by monitoring key deletions.
 *
 * @param {IndexedDBTamperDetectionOptions} options - Configuration options for monitoring.
 */
export function useIndexedDBTamperDetection({
  dbName,
  storeName,
  keysToMonitor,
  pollInterval = 5000,
  onTamperDetected,
}: {
  dbName: string;
  storeName: string;
  keysToMonitor: string[];
  pollInterval?: number;
  onTamperDetected?: (key: string) => void;
}) {
  const baselineRef = useRef<Record<string, unknown>>({});

  useEffect(() => {
    const getValue = (db: IDBDatabase, key: string): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readonly");
        const store = tx.objectStore(storeName);
        const request = store.get(key);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    };

    const openDB = (): Promise<IDBDatabase> => {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    };

    const checkForTampering = async () => {
      try {
        const db = await openDB();

        for (const key of keysToMonitor) {
          const value = await getValue(db, key);

          // First read: establish baseline
          if (!(key in baselineRef.current)) {
            baselineRef.current[key] = value;
            continue;
          }

          const previousValue = baselineRef.current[key];

          // Only warn if it had a value before and is now null/undefined
          if (
            previousValue !== null &&
            previousValue !== undefined &&
            (value === null || value === undefined)
          ) {
            console.warn(
              `[TAMPER ALERT] IndexedDB key "${key}" has been deleted or nulled.`,
              { before: previousValue, after: value }
            );

            // Optional callback (e.g., force logout)
            if (onTamperDetected) {
              onTamperDetected(key);
            }
          } else {
            console.log(`IndexedDB key "${key}" is unchanged.`, {
              before: previousValue,
              after: value,
            });
          }

          // Update baseline after every check
          baselineRef.current[key] = value;
        }
      } catch (err) {
        console.error("IndexedDB Tamper Detection Error:", err);
      }
    };

    // Initial baseline load
    checkForTampering();

    // Start polling loop
    const intervalId: NodeJS.Timeout = setInterval(
      checkForTampering,
      pollInterval
    );

    // Cleanup on unmount
    return () => clearInterval(intervalId);
  }, [dbName, storeName, keysToMonitor, pollInterval, onTamperDetected]);
}
